import React from 'react';
import { Alert, Box, Typography } from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  FitnessCenter as WorkoutIcon,
  InfoOutlined as InfoIcon,
  Restaurant as FoodIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { clientAppPreview as c } from '@/styles/tokens';
import { CalendarEventDto } from '@/types/calendar';

// "Як у додатку" для продакт-менеджера (2026-10-10): приблизна копія вікон деталей мобільного додатку клієнта
// (app/src/app/pages/main/home/food/food-details-dialog, .../activity/activity-details-dialog), щоб бачити суть того,
// що бачить клієнт. Логіка значень повторює додаток: усе береться лише з details (rawInput, components, totals); якщо
// totals немає - додаток показує нулі (FoodTotals / WorkoutTotals за замовчуванням). Вигляд - не піксель у піксель.
// Якщо в додатку зміниться вікно - оновити й тут (і навпаки), див. workspace/conventions/cross-repo-sync.md.

const DISCLAIMER = 'Довідкова інформація, не медичний діагноз чи лікування';

// formatNumber з додатку: ціле - як є, інакше до 0.1.
const formatNumber = (value: number) => (Number.isFinite(value) ? String(Math.round(value * 10) / 10) : '0');

const scheduledTime = (ldt: string | null) =>
  ldt ? new Date(ldt).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' }) : '';

const WORKOUT_TYPES: Record<string, string> = {
  strength: 'Силове',
  cardio: 'Кардіо',
  stretching: 'Розтяжка',
  mobility: 'Мобільність',
  mixed: 'Змішане',
  other: 'Інше',
};

const INTENSITY: Record<string, string> = { low: 'Легка', medium: 'Середня', high: 'Висока' };

const Divider = () => <Box sx={{ height: '1px', bgcolor: c.divider, my: 2 }} />;

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontSize: 16, fontWeight: 700, color: c.textStrong, mb: 1 }}>{children}</Typography>
);

const Header: React.FC<{ event: CalendarEventDto; kind: 'food' | 'workout'; fallbackTitle: string }> = ({ event, kind, fallbackTitle }) => {
  const palette = c[kind];
  const Icon = kind === 'food' ? FoodIcon : WorkoutIcon;
  const rawInput: string = event.details?.rawInput || '';
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: palette.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon sx={{ fontSize: 26, color: palette.accent }} />
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'inline-block', px: 1.25, py: 0.25, borderRadius: 999, bgcolor: palette.badgeBg, color: palette.accent, fontSize: 13, fontWeight: 600 }}>
            {kind === 'food' ? 'Харчування' : 'Тренування'}
          </Box>
          <Typography sx={{ fontSize: 14, color: c.textMuted, mt: 0.25 }}>{event.isCompleted ? 'Виконано' : 'Заплановано'}</Typography>
        </Box>
        {/* Кнопки додатку - лише для вигляду, у кабінеті продакта неактивні */}
        <EditIcon sx={{ color: c.textFaint, fontSize: 20 }} />
        <CloseIcon sx={{ color: c.textFaint, fontSize: 20 }} />
      </Box>

      <Typography sx={{ fontSize: 20, fontWeight: 700, color: c.textStrong, mt: 2 }}>{event.title || fallbackTitle}</Typography>
      {rawInput && <Typography sx={{ fontSize: 15, color: c.textMuted, mt: 0.5, fontStyle: 'italic' }}>«{rawInput}»</Typography>}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: c.textMuted, mt: 1 }}>
        <ScheduleIcon sx={{ fontSize: 18 }} />
        <Typography sx={{ fontSize: 14 }}>{scheduledTime(event.scheduledLdt)}</Typography>
      </Box>

      <Divider />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: c.textFaint }}>
        <InfoIcon sx={{ fontSize: 16 }} />
        <Typography sx={{ fontSize: 12 }}>Джерела даних</Typography>
      </Box>
      <Typography sx={{ fontSize: 11, color: c.textFaint, mt: 0.5 }}>{DISCLAIMER}</Typography>
    </>
  );
};

const Tile: React.FC<{ value: string; label: string; color?: string }> = ({ value, label, color }) => (
  <Box sx={{ flex: 1, textAlign: 'center' }}>
    <Typography sx={{ fontSize: 16, fontWeight: 700, color: color ?? c.textStrong }}>{value}</Typography>
    <Typography sx={{ fontSize: 12, color: c.textMuted }}>{label}</Typography>
  </Box>
);

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ p: 1.5, bgcolor: c.cardBg, border: `1px solid ${c.cardBorder}`, borderRadius: '12px' }}>{children}</Box>
);

const FoodPreview: React.FC<{ event: CalendarEventDto }> = ({ event }) => {
  const components: any[] = event.details?.components || [];
  const totals = event.details?.totals || { kcal: 0, protein: 0, fat: 0, carbs: 0 };
  const portionLabel = (comp: any) =>
    comp.portionName
      ? `${formatNumber(comp.portionCount || 1)} ${comp.portionName}`
      : `${formatNumber(comp.totalAmount || 0)} ${comp.unit || ''}`.trim();
  const macro = (comp: any, key: string) => formatNumber(((comp.nutrition?.[key] || 0) * (comp.totalAmount || 0)) / 100);
  const showDetails = components.some((comp) => !!comp?.nutrition);

  return (
    <>
      <Header event={event} kind="food" fallbackTitle="Їжа" />
      <Box sx={{ display: 'flex', mt: 2 }}>
        <Tile value={String(totals.kcal)} label="ккал" color={c.macros.kcal} />
        <Tile value={`${totals.protein}г`} label="білки" color={c.macros.protein} />
        <Tile value={`${totals.fat}г`} label="жири" color={c.macros.fat} />
        <Tile value={`${totals.carbs}г`} label="вуглеводи" color={c.macros.carbs} />
      </Box>
      <Divider />
      {components.length > 0 && (
        <>
          <SectionTitle>Склад</SectionTitle>
          {components.map((comp, idx) => (
            <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
              <Typography sx={{ fontSize: 15, color: c.textStrong }}>{comp.name}</Typography>
              <Typography sx={{ fontSize: 13, color: c.textMuted }}>{portionLabel(comp)}</Typography>
            </Box>
          ))}
        </>
      )}
      {showDetails && components.length > 0 && (
        <>
          <Divider />
          <SectionTitle>Детально</SectionTitle>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {components.map((comp, idx) => (
              <Card key={idx}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{comp.name}</Typography>
                  <Typography sx={{ fontSize: 12, color: c.textFaint }}>{comp.totalAmount} {comp.unit}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5, fontSize: 12, color: c.textMuted }}>
                  <span>{Math.round(((comp.nutrition?.caloriesPer100 || 0) * (comp.totalAmount || 0)) / 100)} ккал</span>
                  <span>Б {macro(comp, 'proteinPer100')}г</span>
                  <span>Ж {macro(comp, 'fatPer100')}г</span>
                  <span>В {macro(comp, 'carbsPer100')}г</span>
                </Box>
              </Card>
            ))}
          </Box>
        </>
      )}
    </>
  );
};

const WorkoutPreview: React.FC<{ event: CalendarEventDto }> = ({ event }) => {
  const components: any[] = event.details?.components || [];
  const totals = event.details?.totals || { kcal: 0 };
  const amountLabel = (comp: any) => `${formatNumber(comp.totalAmount ?? 0)} ${comp.unit || ''}`.trim();
  const compKcal = (comp: any) => {
    const perUnit = comp.caloriesBurn?.perUnit;
    return perUnit === null || perUnit === undefined ? '—' : formatNumber(perUnit * (comp.totalAmount ?? 0));
  };

  return (
    <>
      <Header event={event} kind="workout" fallbackTitle="Тренування" />
      <Box sx={{ display: 'flex', mt: 2 }}>
        <Tile value={totals.kcal === null || totals.kcal === undefined ? '—' : formatNumber(totals.kcal)} label="ккал" color={c.macros.kcal} />
        <Tile value={String(components.length)} label="вправ" />
      </Box>
      <Divider />
      {components.length > 0 && (
        <>
          <SectionTitle>Склад</SectionTitle>
          {components.map((comp, idx) => (
            <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
              <Box>
                <Typography sx={{ fontSize: 15, color: c.textStrong }}>{comp.name}</Typography>
                <Typography sx={{ fontSize: 12, color: c.textMuted }}>
                  {WORKOUT_TYPES[comp.type] ?? comp.type ?? 'Інше'} · {INTENSITY[comp.intensity] ?? comp.intensity ?? '—'}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 13, color: c.textMuted }}>{amountLabel(comp)}</Typography>
            </Box>
          ))}
          <Divider />
          <SectionTitle>Витрати</SectionTitle>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {components.map((comp, idx) => (
              <Card key={idx}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{comp.name}</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{compKcal(comp)} ккал</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: c.textMuted, mt: 0.5 }}>
                  <span>Одиниця</span><b>{amountLabel(comp)}</b>
                </Box>
                {comp.caloriesBurn?.perUnit != null && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: c.textMuted }}>
                    <span>Ккал / одиницю</span><b>{comp.caloriesBurn.perUnit}</b>
                  </Box>
                )}
                {comp.caloriesBurn?.metValue != null && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: c.textMuted }}>
                    <span>MET</span><b>{comp.caloriesBurn.metValue}</b>
                  </Box>
                )}
              </Card>
            ))}
          </Box>
        </>
      )}
    </>
  );
};

export const PmClientAppPreview: React.FC<{ event: CalendarEventDto }> = ({ event }) => {
  const code = event.eventType.code;
  if (code !== 'food' && code !== 'workout') {
    return (
      <Alert severity="info" variant="outlined">
        Превʼю «як у додатку» поки є лише для харчування і тренувань.
      </Alert>
    );
  }

  const noTotals = !event.details?.totals;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
      {noTotals && (
        <Alert severity="warning" variant="outlined" sx={{ alignSelf: 'stretch' }}>
          У події немає <code>details.totals</code>, тому додаток показує клієнту нулі в підсумках, хоча денний баланс може
          рахувати інакше (див. вкладку «Подія» → «Енергія»).
        </Alert>
      )}
      <Box
        sx={{
          width: 360,
          maxWidth: '100%',
          border: `8px solid ${c.frameBorder}`,
          borderRadius: '28px',
          bgcolor: c.background,
          color: c.text,
          p: 2,
          overflow: 'hidden',
        }}
      >
        {code === 'food' ? <FoodPreview event={event} /> : <WorkoutPreview event={event} />}
      </Box>
      <Typography variant="caption" color="text.secondary">
        Приблизний вигляд вікна деталей у додатку клієнта (з головної). Кнопки неактивні.
      </Typography>
    </Box>
  );
};
